import type { PurchasedProduct } from "@/hooks/usePurchasedProducts";
import type { SubscriptionDeepLinkAction } from "@/lib/subscriptionActionDeepLink";
import { getAllowedPauseMonths } from "./pause-dates";

export type UnavailableReason =
  | "no-active-subscriptions"
  | "subscription-not-found"
  | "cancelled"
  | "already-paused"
  | "not-paused"
  | "no-skips-left"
  | "flexible-plan"
  | "cancellation-requested"
  | "cancel-scheduled";

export function hasSubscription(p: PurchasedProduct): boolean {
  return (
    p.is_subscription === true ||
    p.shopify_subscription_id != null ||
    (p.subscription_status ?? null) != null
  );
}

function statusLower(p: PurchasedProduct): string {
  return p.subscription_status?.toLowerCase() ?? "";
}

export function isActiveSubscription(p: PurchasedProduct): boolean {
  return hasSubscription(p) && statusLower(p) === "active";
}

export function canSkipNextOrder(p: PurchasedProduct): boolean {
  return (
    hasSubscription(p) &&
    statusLower(p) === "active" &&
    p.subscription_is_flexible !== true &&
    p.can_skip_next_order === true &&
    !p.subscription_cancellation_requested_at &&
    !p.subscription_cancel_scheduled_at
  );
}

export function canPauseSubscription(p: PurchasedProduct): boolean {
  return (
    hasSubscription(p) &&
    statusLower(p) === "active" &&
    p.subscription_is_flexible !== true &&
    !p.subscription_cancellation_requested_at &&
    !p.subscription_cancel_scheduled_at &&
    getAllowedPauseMonths(p.subscription_last_charge_date).length > 0
  );
}

export function isEligibleForDeepLink(
  p: PurchasedProduct,
  action: SubscriptionDeepLinkAction,
): boolean {
  return action === "skip-next-order"
    ? canSkipNextOrder(p)
    : canPauseSubscription(p);
}

export function getEligibleSubscriptions(
  products: PurchasedProduct[],
  action: SubscriptionDeepLinkAction,
): PurchasedProduct[] {
  return products.filter((p) => isEligibleForDeepLink(p, action));
}

export function describeIneligibility(
  p: PurchasedProduct | null | undefined,
  action: SubscriptionDeepLinkAction,
): UnavailableReason {
  if (!p) return "subscription-not-found";
  const status = statusLower(p);
  if (!hasSubscription(p)) return "subscription-not-found";
  if (status === "cancelled" || status === "canceled") return "cancelled";
  if (p.subscription_cancellation_requested_at) return "cancellation-requested";
  if (p.subscription_cancel_scheduled_at) return "cancel-scheduled";
  if (p.subscription_is_flexible === true) return "flexible-plan";
  if (action === "pause-subscription") {
    if (status === "paused") return "already-paused";
    if (getAllowedPauseMonths(p.subscription_last_charge_date).length === 0) {
      return "no-skips-left";
    }
  }
  if (action === "skip-next-order") {
    if (status === "paused") return "already-paused";
    if (p.can_skip_next_order !== true) return "no-skips-left";
  }
  return "subscription-not-found";
}
